/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

import '../sass/app.scss'

import Vue from 'vue'
import store from './store'
import router from './router'
import auth from './services/auth'
import api from './api'
import './filters.js'

Vue.prototype.$api = api

import { BootstrapVue, IconsPlugin } from 'bootstrap-vue'

import VueAuth from '@websanova/vue-auth'
import VueAxios from 'vue-axios'
import axios from 'axios'

import Locale from './locales'

import VueI18n from 'vue-i18n'

Vue.router = router
Vue.use(VueAxios, axios)
Vue.use(VueAuth, auth)

Vue.use(BootstrapVue)
Vue.use(IconsPlugin)

Vue.use(VueI18n)
import VueGtag from 'vue-gtag'

import sk from 'vee-validate/dist/locale/sk'
import VeeValidate, { Validator } from 'vee-validate'

Vue.use(VeeValidate, {
  fieldsBagName: 'vvFields',
  classes: true,
  classNames: {
    touched: 'touched', // the control has been blurred
    untouched: 'untouched', // the control hasn't been blurred
    valid: 'is-valid', // model is valid
    invalid: 'is-invalid', // model is invalid
    pristine: 'pristine', // control has not been interacted with
    dirty: 'dirty', // control has been interacted with
  },
})
Validator.localize('sk', sk)

// DIALOG WINDOW
import VueSweetalert2 from 'vue-sweetalert2'

Vue.use(VueSweetalert2, {
  showConfirmButton: false,
  confirmButtonColor: '#71a241',
  cancelButtonColor: '#747474',
  confirmButtonText: 'Áno',
  cancelButtonText: 'Nie',
})

import Vuebar from 'vuebar'

Vue.use(Vuebar)

import VueMoment from 'vue-moment'

import moment from 'moment'
import 'moment/dist/locale/sk.js'

Vue.use(VueMoment, { moment })

import 'vue-awesome/icons'
import Icon from 'vue-awesome/components/Icon'

// eslint-disable-next-line vue/multi-word-component-names
Vue.component('Icon', Icon)

import VueAutonumeric from 'vue-autonumeric'

Vue.component('VueAutonumeric', {
  extends: VueAutonumeric,
  props: {
    value: {
      default: null,
    },
    options: {
      default: function () {
        return {
          decimalCharacter: ',',
          digitGroupSeparator: ' ',
          emptyInputBehavior: 'null',
        }
      },
    },
  },
})

import Highcharts from 'highcharts'
import stockInit from 'highcharts/modules/stock'
import loadExporting from 'highcharts/modules/exporting'
import boost from 'highcharts/modules/boost'

stockInit(Highcharts)
loadExporting(Highcharts)
boost(Highcharts)
Highcharts.setOptions({
  credits: {
    enabled: false,
  },
  chart: {
    style: {
      fontFamily: 'Roboto',
    },
  },
  colors: [
    '#6fa55a',
    '#eeb04d',
    '#cc423f',
    '#3c6c9d',
    '#368f8b',
    '#f46036',
    '#b76935',
    '#00a5cf',
    '#e05780',
    '#3e5c76',
  ],
  plotOptions: {
    series: {
      dataLabels: {
        style: {
          textOutline: 'none',
          fontWeight: 'normal',
        },
      },
    },
  },
  time: {
    timezone: 'Europe/Bratislava',
    useUTC: false,
  },
  lang: {
    downloadPNG: 'Exportovať ako obrázok PNG',
    downloadPDF: 'Exportovať do súboru PDF',
    months: moment.localeData('sk').months(),
    shortMonths: moment.localeData('sk').monthsShort(),
    weekdays: moment.localeData('sk').weekdays(),
    decimalPoint: ',',
  },
})

import VueApexCharts from 'vue-apexcharts'

Vue.use(VueApexCharts)

// eslint-disable-next-line vue/multi-word-component-names
Vue.component('Apexchart', VueApexCharts)

const dateTimeFormats = {
  en: {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  sk: {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
}

const i18n = new VueI18n({
  locale: 'sk', // set locale
  messages: Locale, // set locale messages
  dateTimeFormats,
})

import App from './App.vue'

Vue.prototype.$helpers = {
  months() {
    return moment
      .localeData('en')
      .months()
      .map((m) => {
        return m.toLowerCase()
      })
  },
  monthsSk() {
    return moment.localeData('sk').months()
  },
  monthNames(val) {
    return moment().month(val).locale('sk').format('MM - MMMM')
  },
  datesBetween(range, by = 'days', format = 'YYYY-MM-DD', sortBy = 'asc') {
    const getDaysBetweenDates = (startDate, endDate) => {
      let now = startDate.clone(),
        dates = []

      while (now.isSameOrBefore(endDate)) {
        dates.push(now.format(format))
        now.add(1, by)
      }
      return dates.sort((a, b) =>
        moment(sortBy === 'asc' ? a : b, format).diff(sortBy === 'asc' ? b : a),
      )
    }

    return getDaysBetweenDates(range[0], range[1])
  },
  generatePass() {
    let length = 10
    let chars = 'abcdefghijklmnopqrstuvwxyz*-+ABCDEFGHIJKLMNOP1234567890'
    let pass = ''
    for (let x = 0; x < length; x++) {
      let i = Math.floor(Math.random() * chars.length)
      pass += chars.charAt(i)
    }
    return pass
  },
}

Vue.use(VueGtag, {
  config: {
    id: 'UA-23878379-10',
  },
})
import { mapState } from 'vuex'

api
  .get('auth/user')
  .then((res) => {
    if (res) {
      store.dispatch('user/setUser', res.data.data)
      store.dispatch('user/initApi')
    }
  })
  .catch(() => {})
  .finally(() => {
    new Vue({
      el: '#app',
      data() {
        return {
          loading: false,
        }
      },
      computed: {
        ...mapState({
          userData: (state) => state.user.current,
        }),
        user() {
          return {
            isAdmin: this.$store.getters['user/isAdmin'],
            data: this.$store.getters['user/get'],
          }
        },
      },
      store,
      i18n,
      router,
      render: (h) => h(App),
    })
    store.dispatch('user/setAuthStatus', {})
  })
